import {RichMessageText, TokenSpan} from "./RichMessageText";
import type {Diagnostic, DiagnosticSeverity, ObjectRef} from "./types";

const severityAccent: Record<string, string> = {
  INFO: "border-l-brand-400",
  WARNING: "border-l-amber-500",
  ERROR: "border-l-ots-red-600",
  CRITICAL: "border-l-ots-red-800 bg-ots-red-50 dark:bg-ots-red-950/30"
};

export type DiagnosticRowProps = {
  diagnostic: Diagnostic;
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
};

/**
 * One finding: what happened, the evidence, and what to do about it.
 *
 * Two independent click targets, and the distinction matters. The row follows the diagnostic's
 * primary target. A token inside the sentence follows its own link, which often points somewhere
 * else -- in "ticket X used missing site Y", the ticket and the site are different destinations.
 * Token clicks stop propagating so the row does not also fire.
 */
export const DiagnosticRow = ({diagnostic, canNavigate, onNavigate}: DiagnosticRowProps) => {
  const core = diagnostic.core;
  if (!core) {
    return null;
  }

  const severity = (core.severity ?? "WARNING") as DiagnosticSeverity;
  const primary = core.targets?.[0];
  const rowClickable = Boolean(primary && onNavigate && (!canNavigate || canNavigate(primary)));
  const occurrences = core.occurrenceCount ?? 1;

  return (
    <div
      role={rowClickable ? "button" : undefined}
      tabIndex={rowClickable ? 0 : undefined}
      onClick={rowClickable ? () => onNavigate?.(primary as ObjectRef) : undefined}
      onKeyDown={rowClickable
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onNavigate?.(primary as ObjectRef);
            }
          }
        : undefined}
      className={[
        "rounded-md border border-l-4 border-brand-200 bg-white p-3 dark:border-brand-800 dark:bg-brand-950",
        severityAccent[severity] ?? severityAccent["WARNING"],
        // Every row highlights on hover, not just navigable ones: the reader is tracking which
        // finding they are on, which is worth showing whether or not it can be clicked through.
        "hover:bg-brand-50 dark:hover:bg-brand-900",
        rowClickable ? "cursor-pointer" : ""
      ].join(" ")}>

      {core.title ? (
        <div className="mb-1 flex items-center gap-2">
          <span className="font-semibold text-brand-900 dark:text-brand-100">{core.title}</span>
          {occurrences > 1 ? (
            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-800 dark:text-brand-200">
              &times;{occurrences}
            </span>
          ) : null}
          {core.category ? (
            <span className="text-xs text-brand-600 dark:text-brand-300">{core.category}</span>
          ) : null}
        </div>
      ) : null}

      <RichMessageText
        message={core.message}
        expanded
        canNavigate={canNavigate}
        onNavigate={onNavigate}
        className="text-sm leading-relaxed text-brand-800 dark:text-brand-200"/>

      {core.message?.facts?.length ? (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm">
          {core.message.facts.map((fact, index) => (
            <div key={`${fact.label ?? ""}-${index}`} className="contents">
              <dt className="text-brand-600 dark:text-brand-300">{fact.label}</dt>
              <dd>
                <TokenSpan token={fact.value} canNavigate={canNavigate} onNavigate={onNavigate}/>
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {core.remediation?.message ? (
        <div className="mt-2 border-t border-dashed border-brand-200 pt-2 text-sm text-brand-700 dark:border-brand-800 dark:text-brand-300">
          <RichMessageText message={core.remediation.message} expanded/>
        </div>
      ) : null}
    </div>
  );
};
